import { DateRangePickerProps } from '@amzn/awsui-components-console/date-range-picker';
import {
  HOURS_PER_DAY,
  MILLIS_PER_MINUTE,
  MINUTES_PER_HOUR,
} from 'src/common/constants';

/** Placeholder for a date or duration that does not exist. 'NA', not an em dash: the absence is a
 * definite outcome (a cancelled or failed job has no delivery date), where an em dash reads as
 * empty/unknown. The em dash is still the placeholder elsewhere, e.g. features/namespace/utils.ts. */
export const NOT_AVAILABLE = 'NA';

/**
 * "DD Mon YYYY" formatter. `en-GB` orders the fields day-month-year, so no month
 * list lives here; it renders September as "Sept", which is accepted. Built once:
 * constructing it is the expensive part, and it runs per table cell.
 */
const LOCAL_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

/** "02 Aug 2026" in the local timezone. A named month because "02/08/2026" reads as
 *  8 February to a US reader and 2 August to a UK one, and LangHub has both. */
export const formatDayMonthYear = (date: Date): string =>
  LOCAL_DATE_FORMAT.format(date);

/**
 * Format an epoch-milliseconds timestamp (as string or number) to
 * "DD Mon YYYY", or `NOT_AVAILABLE` when there is no timestamp. All LangHub API
 * timestamps are epoch milliseconds.
 */
export const formatEpochMillisDate = (
  epochMillis: string | number | null | undefined,
): string => {
  if (!epochMillis) return NOT_AVAILABLE;
  return formatDayMonthYear(new Date(Number(epochMillis)));
};

/**
 * Turnaround between two epoch-millisecond timestamps, or `NOT_AVAILABLE` without
 * a completion timestamp. Rolls into days at 24h ("20d 0h 00m", not an unreadable
 * "480h 00m"); below that, "3h 05m". Clamped at zero so upstream clock skew does
 * not render a negative duration.
 */
export const computeTurnaround = (
  sent: string | number,
  completed: string | number | null | undefined,
): string => {
  if (!completed) return NOT_AVAILABLE;
  const elapsedMillis = Math.max(0, Number(completed) - Number(sent));
  const totalMinutes = Math.floor(elapsedMillis / MILLIS_PER_MINUTE);
  const totalHours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = String(totalMinutes % MINUTES_PER_HOUR).padStart(2, '0');
  const days = Math.floor(totalHours / HOURS_PER_DAY);
  if (days === 0) return `${totalHours}h ${minutes}m`;
  return `${days}d ${totalHours % HOURS_PER_DAY}h ${minutes}m`;
};

/** Validates that the selected range is within the last year and not in the future. */
export const isValidRange = (
  range: DateRangePickerProps.Value | null,
): DateRangePickerProps.ValidationResult => {
  if (range === null) return { valid: true };

  const now = new Date();
  now.setHours(23, 59, 59, 999); // end of today — allows today's date to pass

  const oneYearAgo = new Date();
  oneYearAgo.setHours(0, 0, 0, 0); // start of today, then shift back 1 year
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  if (range.type === 'absolute') {
    if (!range.startDate || !range.endDate) {
      return { valid: false, errorMessage: 'Select both start and end dates.' };
    }
    if (range.startDate > range.endDate) {
      return {
        valid: false,
        errorMessage: 'End date must be equal to or after start date.',
      };
    }
    if (new Date(range.startDate) < oneYearAgo) {
      return {
        valid: false,
        errorMessage: 'Start date cannot be more than 1 year in the past.',
      };
    }
    if (new Date(range.endDate) > now) {
      return {
        valid: false,
        errorMessage: 'End date cannot be in the future.',
      };
    }
  }

  if (range.type === 'relative') {
    const totalDays =
      range.unit === 'day'
        ? range.amount
        : range.unit === 'week'
          ? range.amount * 7
          : range.unit === 'month'
            ? range.amount * 30
            : range.amount * 365;
    if (totalDays > 365) {
      return {
        valid: false,
        errorMessage: 'Relative range cannot exceed 1 year.',
      };
    }
  }

  return { valid: true };
};

/** Returns a reason string if date is disabled, empty string otherwise. */
export const dateDisabledReason = (date: Date): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  if (date > today) return 'Future dates are not allowed.';
  if (date < oneYearAgo) return 'Dates older than 1 year are not allowed.';
  return '';
};

/** Returns true if the date is selectable (within last year, not future). */
export const isDateEnabled = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  return date >= oneYearAgo && date <= today;
};
